(function() {
  'use strict';

  var CustomTheme = (function() {
    var instance = null;
    var boundHandlers = {};
    var resizeHandler = null;
    var observers = [];
    var cache = {};

    var config = {
      flickity: {
        friction: 0.28,
        selectedAttraction: 0.2,
        enabled: true,
        excludeSelectors: []
      },
      selectors: {
        announcementBar: '.announcement-bar',
        siteHeader: '.site-header',
        accountAndSocial: '.account_and_social',
        languageSelector: '#LanguageList',
        navDrawer: '#NavDrawer'
      }
    };

    var handlerId = 0;

    function addBoundHandler(element, event, fn) {
      element.addEventListener(event, fn);
      var id = 'handler_' + (++handlerId);
      boundHandlers[id] = {
        element: element,
        event: event,
        fn: fn
      };
      return id;
    }

    function getInstance() {
      if (!instance) {
        instance = createInstance();
      }
      return instance;
    }

    function createInstance() {
      return {
        init: init,
        reinit: reinit,
        destroy: destroy,
        getConfig: function() { return Object.assign({}, config); }
      };
    }

    function getElements() {
      if (!cache.initialized) {
        cache = {
          initialized: true,
          announcementBar: document.querySelector(config.selectors.announcementBar),
          siteHeader: document.querySelector(config.selectors.siteHeader),
          accountAndSocial: document.querySelector(config.selectors.accountAndSocial),
          languageSelector: document.querySelector(config.selectors.languageSelector),
          navDrawer: document.querySelector(config.selectors.navDrawer)
        };
      }
      return cache;
    }

    function clearCache() {
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
        if (resizeHandler.timeout) {
          clearTimeout(resizeHandler.timeout);
        }
        resizeHandler = null;
      }
      cache = { initialized: false };
    }

    function init() {
      if (cache.initialized) return;

      initAnnouncementBar();
      initMobileLanguageSelector();
      initMobileNavClickArea();
      scrollActiveNavToCenter();

      cache.initialized = true;
    }

    function reinit() {
      clearCache();
      init();
    }

    function destroy() {
      for (var i = 0; i < observers.length; i++) {
        if (observers[i] && observers[i].disconnect) {
          observers[i].disconnect();
        }
      }
      observers = [];

      Object.keys(boundHandlers).forEach(function(key) {
        var handler = boundHandlers[key];
        if (handler && handler.element && handler.event && handler.fn) {
          handler.element.removeEventListener(handler.event, handler.fn);
        }
      });
      boundHandlers = {};

      clearCache();
      instance = null;
    }

    function initAnnouncementBar() {
      var els = getElements();
      if (!els.announcementBar) return;

      var section = els.announcementBar.closest('.shopify-section');
      if (!section) return;

      section.classList.add('announcement-bar-sticky');

      var updateHeight = function(height) {
        document.documentElement.style.setProperty('--announcement-height', height + 'px');
      };

      if (typeof ResizeObserver !== 'undefined') {
        var observer = new ResizeObserver(function(entries) {
          if (entries[0]) {
            requestAnimationFrame(function() {
              updateHeight(entries[0].contentRect.height);
            });
          }
        });
        observer.observe(section);
        observers.push(observer);
      } else {
        var update = function() {
          updateHeight(section.offsetHeight);
        };
        update();

        resizeHandler = function() {
          clearTimeout(resizeHandler.timeout);
          resizeHandler.timeout = setTimeout(update, 100);
        };
        window.addEventListener('resize', resizeHandler);
      }
    }

    function getAvailableLanguages() {
      var languages = [];

      var langSelector = document.querySelector(config.selectors.languageSelector);
      if (langSelector) {
        var options = langSelector.querySelectorAll('option');
        options.forEach(function(option) {
          var code = option.value || option.getAttribute('lang');
          var name = option.textContent.trim();
          if (code && name) {
            languages.push({
              code: code,
              name: name
            });
          }
        });
      }

      if (languages.length === 0) {
        var localizationForm = document.querySelector('form[action*="localization"]');
        if (localizationForm) {
          var inputs = localizationForm.querySelectorAll('input[name="locale_code"]');
          inputs.forEach(function(input) {
            var code = input.value;
            if (code) {
              languages.push({
                code: code,
                name: getLanguageName(code)
              });
            }
          });
        }
      }

      if (languages.length <= 1) {
        var currentLocale = document.documentElement.lang || 'en';
        var currentLang = currentLocale.split('-')[0];

        languages = [
          { code: 'en', name: 'English' },
          { code: 'fr', name: 'Français' }
        ];

        var hasCurrentLang = languages.some(function(lang) {
          return lang.code === currentLang;
        });

        if (!hasCurrentLang && currentLang !== 'en' && currentLang !== 'fr') {
          languages.push({
            code: currentLang,
            name: getLanguageName(currentLang)
          });
        }
      }

      return languages;
    }

    function getLanguageName(code) {
      var names = {
        'en': 'English',
        'fr': 'Français',
        'de': 'Deutsch',
        'es': 'Español',
        'it': 'Italiano',
        'pt': 'Português',
        'nl': 'Nederlands',
        'ja': '日本語',
        'zh': '中文',
        'ko': '한국어',
        'ar': 'العربية',
        'ru': 'Русский'
      };
      return names[code] || code.toUpperCase();
    }

    function getLanguageFlagClass(code) {
      var flagMap = {
        'en': 'mobile-language-selector__flag--us',
        'fr': 'mobile-language-selector__flag--fr',
        'de': 'mobile-language-selector__flag--de',
        'es': 'mobile-language-selector__flag--es',
        'it': 'mobile-language-selector__flag--it',
        'pt': 'mobile-language-selector__flag--pt',
        'nl': 'mobile-language-selector__flag--nl',
        'ja': 'mobile-language-selector__flag--jp',
        'zh': 'mobile-language-selector__flag--cn',
        'ko': 'mobile-language-selector__flag--kr'
      };
      return flagMap[code] || 'mobile-language-selector__flag--default';
    }

    function initMobileLanguageSelector() {
      var els = getElements();
      if (!els.accountAndSocial) return;
      if (document.querySelector('.mobile-language-selector')) return;

      var languages = getAvailableLanguages();

      var classList = els.accountAndSocial.classList;
      var delayClass = 'appear-delay-12';
      for (var i = 0; i < classList.length; i++) {
        if (classList[i].indexOf('appear-delay-') === 0) {
          delayClass = classList[i];
          break;
        }
      }

      var nextDelay = 'appear-delay-13';
      var match = delayClass.match(/appear-delay-(\d+)/);
      if (match && match[1]) {
        nextDelay = 'appear-delay-' + (parseInt(match[1], 10) + 1);
      }

      var currentLocale = document.documentElement.lang || 'en';
      var currentLang = currentLocale.split('-')[0];

      var itemsHTML = languages.map(function(lang) {
        var flagClass = getLanguageFlagClass(lang.code);
        var activeClass = lang.code === currentLang ? ' mobile-language-selector__item--active' : '';
        return '<div class="mobile-language-selector__item' + activeClass + '" ' +
               'data-lang="' + lang.code + '" ' +
               'role="button" ' +
               'tabindex="0" ' +
               'aria-label="' + lang.name + '">' +
               '<span class="mobile-language-selector__flag ' + flagClass + '" aria-hidden="true"></span>' +
               '<span class="mobile-language-selector__name">' + lang.name + '</span>' +
               '</div>';
      }).join('');

      var selectorHTML = '<div class="mobile-language-selector appear-animation ' + nextDelay + '">' + itemsHTML + '</div>';

      var temp = document.createElement('div');
      temp.innerHTML = selectorHTML.trim();
      var selector = temp.firstChild;

      if (document.body.classList.contains('js-drawer-open')) {
        selector.style.opacity = '1';
        selector.style.transform = 'translateY(0)';
      }

      var clickHandler = function(e) {
        var item = e.target.closest('.mobile-language-selector__item');
        if (!item) return;

        e.preventDefault();
        var selectedLang = item.getAttribute('data-lang');
        if (selectedLang === currentLang) return;

        switchLanguage(selectedLang);
      };
      addBoundHandler(selector, 'click', clickHandler);

      var keydownHandler = function(e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;

        var item = e.target.closest('.mobile-language-selector__item');
        if (!item) return;

        e.preventDefault();
        var selectedLang = item.getAttribute('data-lang');
        if (selectedLang === currentLang) return;

        switchLanguage(selectedLang);
      };
      addBoundHandler(selector, 'keydown', keydownHandler);

      els.accountAndSocial.parentNode.insertBefore(selector, els.accountAndSocial.nextSibling);
    }

    function switchLanguage(langCode) {
      var pathname = window.location.pathname;
      var langMatch = pathname.match(/^\/(en|fr|de|es|it|pt|nl|ja|zh|ko|ar|ru)(\/|$)/);
      var currentLangPath = langMatch ? '/' + langMatch[1] : '';
      var pathWithoutLang = currentLangPath ? pathname.slice(currentLangPath.length) : pathname;

      var form = document.createElement('form');
      form.method = 'POST';
      form.action = '/localization';
      form.style.display = 'none';
      form.setAttribute('accept-charset', 'UTF-8');

      var returnPath = '/' + langCode + pathWithoutLang;
      var fields = [
        { name: 'form_type', value: 'localization' },
        { name: 'utf8', value: '✓' },
        { name: '_method', value: 'put' },
        { name: 'return_to', value: returnPath },
        { name: 'locale_code', value: langCode }
      ];

      fields.forEach(function(field) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = field.name;
        input.value = field.value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    }

    function scrollActiveNavToCenter() {
      var activeItem = document.querySelector('.immersive-bar__nav-item.is-active');
      if (!activeItem) return;

      var nav = activeItem.closest('.immersive-bar__nav');
      if (!nav) return;

      var navRect = nav.getBoundingClientRect();
      var itemRect = activeItem.getBoundingClientRect();
      var scrollLeft = nav.scrollLeft + (itemRect.left - navRect.left) - (navRect.width / 2) + (itemRect.width / 2);

      nav.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }

    function initMobileNavClickArea() {
      var els = getElements();
      if (!els.navDrawer) return;

      var hasSublistItems = els.navDrawer.querySelectorAll('.mobile-nav__has-sublist');
      hasSublistItems.forEach(function(item) {
        var triggerBtn = item.querySelector('.mobile-nav__toggle .collapsible-trigger');
        if (!triggerBtn) return;

        var handler = function(e) {
          if (e.target.closest('.collapsible-trigger')) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          triggerBtn.click();
        };
        addBoundHandler(item, 'click', handler);
      });

      var childItems = els.navDrawer.querySelectorAll('.mobile-nav__child-item');
      childItems.forEach(function(item) {
        var triggerBtn = item.querySelector('.collapsible-trigger');
        if (!triggerBtn || triggerBtn.classList.contains('mobile-nav__link--button')) return;

        var handler = function(e) {
          if (e.target.closest('.collapsible-trigger')) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          triggerBtn.click();
        };
        addBoundHandler(item, 'click', handler);
      });
    }

    return getInstance();
  })();

  var flickityRetryCount = 0;
  var FLICKITY_MAX_RETRY = 50;

  function initFlickityOverride() {
    if (typeof Flickity === 'undefined') {
      flickityRetryCount++;
      if (flickityRetryCount < FLICKITY_MAX_RETRY) {
        setTimeout(initFlickityOverride, 10);
      }
      return;
    }

    var originalFlickity = Flickity;
    var customConfig = {
      friction: 0.28,
      selectedAttraction: 0.2
    };

    window.Flickity = function(element, options) {
      if (!options || typeof options !== 'object') {
        options = {};
      }

      var isExcluded = false;
      if (window.CustomTheme) {
        var config = window.CustomTheme.getConfig();
        if (config.flickity && config.flickity.excludeSelectors) {
          for (var i = 0; i < config.flickity.excludeSelectors.length; i++) {
            if (element.matches && element.matches(config.flickity.excludeSelectors[i])) {
              isExcluded = true;
              break;
            }
          }
        }
      }

      if (!isExcluded) {
        if (options.friction === undefined) {
          options.friction = customConfig.friction;
        }
        if (options.selectedAttraction === undefined) {
          options.selectedAttraction = customConfig.selectedAttraction;
        }
      }

      return new originalFlickity(element, options);
    };

    Object.keys(originalFlickity).forEach(function(key) {
      window.Flickity[key] = originalFlickity[key];
    });

    window.Flickity.prototype = originalFlickity.prototype;
    window.Flickity.defaults = originalFlickity.defaults;
  }

  initFlickityOverride();

  var globalHandlers = {
    domContentLoaded: function() {
      CustomTheme.init();
    },
    sectionLoad: function() {
      CustomTheme.reinit();
    },
    sectionUnload: function() {
      CustomTheme.destroy();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', globalHandlers.domContentLoaded);
  } else {
    CustomTheme.init();
  }

  document.addEventListener('shopify:section:load', globalHandlers.sectionLoad);
  document.addEventListener('shopify:section:unload', globalHandlers.sectionUnload);

  window.CustomTheme = CustomTheme;
  window.CustomTheme._globalHandlers = globalHandlers;

  var ImmersiveOverlay = (function() {
    var overlay;
    var form;
    var isOpen = false;
    var initialized = false;

    function init() {
      overlay = document.getElementById('ImmersiveOverlay');
      if (!overlay) return;

      form = overlay.querySelector('[data-overlay-form]');

      var trigger = document.querySelector('[data-overlay-trigger]');
      var closeBtn = overlay.querySelector('[data-overlay-close]');
      var backdrop = overlay.querySelector('[data-overlay-backdrop]');
      var applyBtn = overlay.querySelector('[data-overlay-apply]');
      var resetBtn = overlay.querySelector('[data-overlay-reset]');

      if (trigger) {
        trigger.addEventListener('click', open);
      }
      if (closeBtn) {
        closeBtn.addEventListener('click', close);
      }
      if (backdrop) {
        backdrop.addEventListener('click', close);
      }
      if (applyBtn) {
        applyBtn.addEventListener('click', apply);
      }
      if (resetBtn) {
        resetBtn.addEventListener('click', reset);
      }

      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && isOpen) {
          close();
        }
      });

      var filterItems = overlay.querySelectorAll('.immersive-overlay__filter-item');
      filterItems.forEach(function(item) {
        item.addEventListener('click', function() {
          var cb = item.querySelector('.immersive-overlay__filter-checkbox');
          if (cb) {
            cb.checked = !cb.checked;
            item.classList.toggle('is-active', cb.checked);
          }
        });
      });

      var collapsibles = overlay.querySelectorAll('[data-overlay-collapsible]');
      collapsibles.forEach(function(trigger) {
        trigger.addEventListener('click', function() {
          var content = trigger.nextElementSibling;
          var isExpanded = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
          if (content) {
            content.classList.toggle('is-open', !isExpanded);
          }
        });

        var content = trigger.nextElementSibling;
        var hasActive = trigger.closest('.immersive-overlay__section').querySelector('.immersive-overlay__filter-item.is-active');
        if (hasActive) {
          trigger.setAttribute('aria-expanded', 'true');
          if (content) {
            content.classList.add('is-open');
          }
        }
      });

      var removeTagBtns = overlay.querySelectorAll('[data-overlay-remove-tag]');
      removeTagBtns.forEach(function(btn) {
        btn.addEventListener('click', function() {
          var param = btn.getAttribute('data-param');
          var value = btn.getAttribute('data-value');
          var checkbox = overlay.querySelector('input[name="' + param + '"][value="' + value + '"]');
          if (checkbox) {
            checkbox.checked = false;
            var parentItem = checkbox.closest('.immersive-overlay__filter-item');
            if (parentItem) {
              parentItem.classList.remove('is-active');
            }
          }
          btn.remove();
          apply();
        });
      });

      var clearAllBtn = overlay.querySelector('[data-overlay-clear-all]');
      if (clearAllBtn) {
        clearAllBtn.addEventListener('click', function() {
          var allCheckboxes = overlay.querySelectorAll('.immersive-overlay__filter-checkbox');
          allCheckboxes.forEach(function(cb) {
            cb.checked = false;
            var parentItem = cb.closest('.immersive-overlay__filter-item');
            if (parentItem) {
              parentItem.classList.remove('is-active');
            }
          });
          apply();
        });
      }

      initialized = true;

      disableOverlayPriceAutoSubmit();
    }

    function disableOverlayPriceAutoSubmit() {
      var overlayPriceRange = overlay.querySelector('[data-overlay-price-range]');
      if (!overlayPriceRange || typeof window.noUiSlider === 'undefined') return;

      var sliderEl = overlayPriceRange.querySelector('.price-range__slider');
      if (!sliderEl || !sliderEl.noUiSlider) return;

      sliderEl.noUiSlider.destroy();

      var minRange = parseFloat(overlayPriceRange.dataset.min) || 0;
      var minValue = parseFloat(overlayPriceRange.dataset.minValue) || 0;
      var maxRange = parseFloat(overlayPriceRange.dataset.max) || 100;
      var maxValue = parseFloat(overlayPriceRange.dataset.maxValue) || maxRange;

      var inputMin = overlayPriceRange.querySelector('.price-range__input-min');
      var inputMax = overlayPriceRange.querySelector('.price-range__input-max');
      var displayMin = overlayPriceRange.querySelector('.price-range__display-min');
      var displayMax = overlayPriceRange.querySelector('.price-range__display-max');

      noUiSlider.create(sliderEl, {
        connect: true,
        step: 10,
        start: [minValue, maxValue],
        range: { min: minRange, max: maxRange }
      });

      sliderEl.noUiSlider.on('update', function(values) {
        if (displayMin) displayMin.innerHTML = theme.Currency.formatMoney(values[0], theme.settings.moneyFormat);
        if (displayMax) displayMax.innerHTML = theme.Currency.formatMoney(values[1], theme.settings.moneyFormat);
      });

      sliderEl.noUiSlider.on('change', function(values) {
        if (inputMin) inputMin.value = values[0];
        if (inputMax) inputMax.value = values[1];
      });
    }

    function open() {
      if (!overlay) return;
      isOpen = true;
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('immersive-overlay-open');
    }

    function close() {
      if (!overlay) return;
      isOpen = false;
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('immersive-overlay-open');
    }

    function apply() {
      if (!form) return;

      var formData = new FormData(form);
      var searchParams = new URLSearchParams();

      var currentUrl = new URL(window.location.href);
      var existingParams = new URLSearchParams(currentUrl.search);

      formData.forEach(function(value, key) {
        if (key === 'sort_by') {
          searchParams.set('sort_by', value);
        } else {
          searchParams.append(key, value);
        }
      });

      if (!searchParams.has('sort_by') && existingParams.has('sort_by')) {
        searchParams.set('sort_by', existingParams.get('sort_by'));
      }

      searchParams.delete('page');

      close();

      window.location.search = searchParams.toString();
    }

    function reset() {
      if (!form) return;

      var allCheckboxes = overlay.querySelectorAll('.immersive-overlay__filter-checkbox');
      allCheckboxes.forEach(function(cb) {
        cb.checked = false;
        var parentItem = cb.closest('.immersive-overlay__filter-item');
        if (parentItem) {
          parentItem.classList.remove('is-active');
        }
      });

      var sortSelect = overlay.querySelector('[data-overlay-sort-select]');
      if (sortSelect) {
        sortSelect.selectedIndex = 0;
      }

      var priceInputs = overlay.querySelectorAll('.price-range__input');
      priceInputs.forEach(function(input) {
        var min = input.closest('.price-range').getAttribute('data-min');
        var max = input.closest('.price-range').getAttribute('data-max');
        if (input.classList.contains('price-range__input-min')) {
          input.value = min;
        } else {
          input.value = max;
        }
      });

      var url = new URL(window.location.href);
      url.search = '';
      close();
      window.location.href = url.toString();
    }

    return { init: init };
  })();

  function initImmersiveOverlay() {
    ImmersiveOverlay.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initImmersiveOverlay);
  } else {
    initImmersiveOverlay();
  }

  document.addEventListener('shopify:section:load', function() {
    setTimeout(initImmersiveOverlay, 100);
  });

  document.addEventListener('collection:reloaded', function() {
    setTimeout(initImmersiveOverlay, 100);
  });

  var CollectionHeaderScroll = (function() {
    var initialized = false;
    var lastScroll = 0;
    var headerVisible = false;
    var ticking = false;
    var filterStuck = false;
    var filterStuckOffset = 0;

    function isCollectionPage() {
      return !!document.querySelector('.collection-filter--immersive');
    }

    function getHeaderHeight() {
      var siteHeader = document.querySelector('.site-header');
      if (!siteHeader) return 0;
      return siteHeader.offsetHeight;
    }

    function getAnnouncementHeight() {
      var val = getComputedStyle(document.documentElement).getPropertyValue('--announcement-height');
      return parseInt(val, 10) || 0;
    }

    function calcFilterStuckOffset() {
      var immersiveFilter = document.querySelector('.collection-filter--immersive');
      if (!immersiveFilter) return 0;
      var stickyTop = getAnnouncementHeight() + getHeaderHeight();
      return immersiveFilter.getBoundingClientRect().top + window.scrollY - stickyTop;
    }

    function setFilterStuck(stuck) {
      if (stuck === filterStuck) return;
      filterStuck = stuck;
      var immersiveFilter = document.querySelector('.collection-filter--immersive');
      if (!immersiveFilter) return;
      immersiveFilter.classList.toggle('collection-filter--immersive--stuck', stuck);
      if (!stuck) {
        immersiveFilter.classList.remove('collection-filter--immersive-with-header');
        immersiveFilter.style.transform = '';
      }
    }

    function showHeader() {
      if (headerVisible) return;
      headerVisible = true;

      var siteHeader = document.querySelector('.site-header');
      var wrapper = document.querySelector('#HeaderWrapper');
      if (!siteHeader) return;

      siteHeader.classList.add('site-header--stuck');
      siteHeader.style.top = getAnnouncementHeight() + 'px';

      if (wrapper && wrapper.classList.contains('is-light')) {
        wrapper.classList.remove('is-light');
      }

      setTimeout(function() {
        siteHeader.classList.add('site-header--opening');
      }, 100);

      if (filterStuck) {
        updateImmersiveFilter(true);
      }
    }

    function hideHeader() {
      if (!headerVisible) return;
      headerVisible = false;

      var siteHeader = document.querySelector('.site-header');
      var wrapper = document.querySelector('#HeaderWrapper');
      if (!siteHeader) return;

      siteHeader.classList.remove('site-header--opening');
      siteHeader.classList.remove('site-header--stuck');
      siteHeader.style.top = '';

      if (wrapper && wrapper.classList.contains('header-wrapper--sticky')) {
        wrapper.classList.add('is-light');
      }

      if (filterStuck) {
        updateImmersiveFilter(false);
      }
    }

    var filterTransitionTimer = null;

    function updateImmersiveFilter(withHeader) {
      var immersiveFilter = document.querySelector('.collection-filter--immersive');
      if (!immersiveFilter) return;

      immersiveFilter.classList.toggle('collection-filter--immersive-with-header', withHeader);

      immersiveFilter.classList.add('collection-filter--immersive--moving');

      if (filterTransitionTimer) clearTimeout(filterTransitionTimer);
      filterTransitionTimer = setTimeout(function() {
        immersiveFilter.classList.remove('collection-filter--immersive--moving');
        filterTransitionTimer = null;
      }, 400);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(function() {
        if (!isCollectionPage()) {
          ticking = false;
          return;
        }

        var currentScroll = window.scrollY;

        setFilterStuck(currentScroll >= filterStuckOffset);

        if (!filterStuck) {
          lastScroll = currentScroll;
          ticking = false;
          return;
        }

        if (currentScroll <= 10) {
          hideHeader();
        } else if (currentScroll < lastScroll) {
          showHeader();
        } else if (currentScroll > lastScroll) {
          hideHeader();
        }

        lastScroll = currentScroll;
        ticking = false;
      });
    }

    function init() {
      if (initialized) return;
      if (!isCollectionPage()) return;

      document.body.classList.add('has-immersive-filter');

      filterStuckOffset = calcFilterStuckOffset();
      lastScroll = window.scrollY;
      window.addEventListener('scroll', onScroll, { passive: true });

      initialized = true;
    }

    function destroy() {
      if (!initialized) return;

      document.body.classList.remove('has-immersive-filter');
      window.removeEventListener('scroll', onScroll);

      var immersiveFilter = document.querySelector('.collection-filter--immersive');
      if (immersiveFilter) {
        immersiveFilter.classList.remove('collection-filter--immersive-with-header');
        immersiveFilter.classList.remove('collection-filter--immersive--moving');
        immersiveFilter.classList.remove('collection-filter--immersive--stuck');
        immersiveFilter.style.transform = '';
      }

      if (filterTransitionTimer) {
        clearTimeout(filterTransitionTimer);
        filterTransitionTimer = null;
      }

      if (headerVisible) {
        var siteHeader = document.querySelector('.site-header');
        if (siteHeader) {
          siteHeader.classList.remove('site-header--opening');
          siteHeader.classList.remove('site-header--stuck');
          siteHeader.style.top = '';
        }
        headerVisible = false;
      }

      filterStuck = false;
      filterStuckOffset = 0;
      initialized = false;
      lastScroll = 0;
    }

    return { init: init, destroy: destroy };
  })();

  function initCollectionHeaderScroll() {
    CollectionHeaderScroll.destroy();
    CollectionHeaderScroll.init();
  }

  function destroyCollectionHeaderScroll() {
    CollectionHeaderScroll.destroy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionHeaderScroll);
  } else {
    initCollectionHeaderScroll();
  }

  document.addEventListener('shopify:section:load', function() {
    setTimeout(initCollectionHeaderScroll, 200);
  });

  document.addEventListener('collection:reloaded', function() {
    setTimeout(initCollectionHeaderScroll, 200);
  });

  var CustomNewsletterPopup = (function() {
    var initialized = false;

    function init() {
      var forms = document.querySelectorAll('.custom-popup form');
      if (!forms.length) return;

      forms.forEach(function(form) {
        if (form.dataset.customPopupBound) return;
        form.dataset.customPopupBound = 'true';

        form.addEventListener('submit', function(e) {
          e.preventDefault();

          var emailInput = form.querySelector('input[type="email"]');
          if (!emailInput || !emailInput.value) return;

          var submitBtn = form.querySelector('.submit-button');
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = '...';
          }

          var formData = new FormData(form);

          fetch(form.action, {
            method: 'POST',
            body: formData,
            headers: {
              'Accept': 'application/json'
            }
          })
          .then(function(response) {
            return response.json();
          })
          .then(function(data) {
            showThankYou(form);
          })
          .catch(function() {
            showThankYou(form);
          });
        });
      });

      initialized = true;
    }

    function showThankYou(form) {
      var wrapper = form.closest('[data-popup-wrapper]');
      if (!wrapper) return;

      var existingThanks = wrapper.querySelector('.pop-thanks-container');
      if (existingThanks) return;

      form.style.display = 'none';

      var imageSpacer = wrapper.querySelector('.block-wrapper[block-type="image-spacer"]');
      if (imageSpacer) imageSpacer.style.display = 'none';

      var blocksRenderer = form.closest('.blocks-renderer');
      if (blocksRenderer) {
        var standaloneBlocks = blocksRenderer.querySelectorAll(':scope > .block-wrapper, :scope > .block-content');
        standaloneBlocks.forEach(function(el) { el.style.display = 'none'; });
      }

      var btnBg = '#000000';
      var btnText = '#ffffff';
      var submitBtn = form.querySelector('.submit-button');
      if (submitBtn) {
        var cs = getComputedStyle(submitBtn);
        if (cs.backgroundColor && cs.backgroundColor !== 'rgba(0, 0, 0, 0)') {
          btnBg = cs.backgroundColor;
        }
        if (cs.color && cs.color !== 'rgba(0, 0, 0, 0)') {
          btnText = cs.color;
        }
      }

      var sectionIdEl = wrapper.closest('[data-section-id]');
      var sectionId = sectionIdEl ? sectionIdEl.getAttribute('data-section-id') : '';

      var thanksHtml = document.createElement('div');
      thanksHtml.className = 'pop-thanks-container';
      thanksHtml.innerHTML =
        '<div class="pop-thanks-text">' +
          '<h1>Thanks for your interest!</h1>' +
        '</div>' +
        '<button type="button" class="submit-button clickable-effect pop-thanks-close-button js-modal-close" style="background-color: ' + btnBg + '; color: ' + btnText + ';">Close</button>';

      wrapper.appendChild(thanksHtml);

      var closeBtn = thanksHtml.querySelector('.js-modal-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', function() {
          var modalEl = wrapper.closest('.modal');
          if (modalEl) {
            var closeTrigger = modalEl.querySelector('.js-modal-close');
            if (closeTrigger) closeTrigger.click();
          }
        });
      }

      if (sectionId) {
        document.dispatchEvent(new CustomEvent('modalClose.' + 'NewsletterPopup-' + sectionId));
      }
    }

    return { init: init };
  })();

  function initCustomNewsletterPopup() {
    CustomNewsletterPopup.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomNewsletterPopup);
  } else {
    initCustomNewsletterPopup();
  }

  document.addEventListener('shopify:section:load', function() {
    setTimeout(initCustomNewsletterPopup, 200);
  });
})();
